import React from "react";
import PropTypes from "prop-types";
import SkeletonList from "../skeletonList";
import { formatClassNames } from "../../../../utils/common";

/**
 * CategorySkeleton component for horizontal category scroll
 */
const CategorySkeleton = () => {
  return (
    <div className="flex w-[100px] shrink-0 flex-col gap-[5px] md:w-[127px]">
      <div className="h-[100px] w-full animate-[pulse_0.6s_ease-in-out_infinite] rounded-[16px] bg-gradient-to-r from-[#e0e0e0] to-[#fafafa] md:h-[127px]" />
      <div className="h-[10px] w-full animate-[pulse_0.6s_ease-in-out_infinite] rounded-[4px] bg-gradient-to-r from-[#e0e0e0] to-[#fafafa] md:h-[12px]" />
    </div>
  );
};

/**
 * ProductCardSkeleton component for loading states.
 * Displays a skeleton loader that matches the product card structure.
 * Fills its container automatically (h-full w-full)
 *
 * @param {Object} props - The component props.
 * @param {string} [props.className=""] - Additional CSS classes for width and layout.
 * @returns {React.ReactElement} The ProductCardSkeleton component.
 */
const ProductCardSkeleton = ({ className = "" }) => {
  return (
    <div className={`relative flex h-full w-full flex-col ${className}`}>
      {/* Image skeleton - maintains square aspect ratio */}
      <div className="relative w-full" style={{ aspectRatio: "1 / 1" }}>
        <SkeletonList
          count={1}
          className="h-full w-full"
          itemClassName="h-full w-full"
          itemStyle={{
            borderRadius: "8px 8px 0 0",
          }}
        />
      </div>
      {/* Text skeletons below image - fills remaining space */}
      <div className="flex flex-1 flex-col justify-start pt-8 sm:p-12">
        {/* First skeleton */}
        <SkeletonList
          count={1}
          className="mb-4"
          itemClassName=""
          itemStyle={{ width: "90%", maxWidth: "236px", height: "12px", borderRadius: "4px" }}
        />
        {/* Second skeleton */}
        <SkeletonList
          count={1}
          className="mb-4"
          itemClassName=""
          itemStyle={{ width: "60%", maxWidth: "142px", height: "22px", borderRadius: "4px" }}
        />
        {/* Third skeleton */}
        <SkeletonList
          count={1}
          className="mb-4"
          itemClassName=""
          itemStyle={{ width: "40%", maxWidth: "88px", height: "22px", borderRadius: "4px" }}
        />
        {/* Fourth skeleton */}
        <SkeletonList
          count={1}
          className=""
          itemClassName=""
          itemStyle={{ width: "55%", maxWidth: "131px", height: "14px", borderRadius: "4px" }}
        />
      </div>
    </div>
  );
};

ProductCardSkeleton.propTypes = {
  className: PropTypes.string,
};

/**
 * PLP Skeleton Component
 * Displays a skeleton loading state for Product Listing Page with header, filters, and product grid
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} [props.showHeader=true] - Whether to show header skeleton
 * @param {boolean} [props.showFilters=true] - Whether to show filter sidebar skeleton
 * @param {number} [props.productCount=12] - Number of product skeleton cards to display
 * @param {boolean} [props.hasBanner=false] - Whether banner is present above header (affects header height)
 * @param {boolean} [props.isHomePage=false] - Homepage nav skeleton; desktop header spacer is 0
 * @returns {JSX.Element}
 */
const PlpSkeleton = ({
  showHeader = true,
  showFilters = true,
  productCount = 12,
  isMobile = false,
  hasBanner = false,
  isHomePage = false,
}) => {
  return (
    <div className="relative min-h-screen bg-gray-50">
      {/* Header Section — homepage desktop already has sticky header offset, so spacer is 0 */}
      {showHeader && (
        <div
          className={formatClassNames(
            "w-full",
            isHomePage && "md:h-[50px]",
            !isHomePage && hasBanner && "md:h-[150px]",
            !isHomePage && !hasBanner && "md:h-[100px]",
          )}
        />
      )}

      {/* Main Content Area */}
      <div className="min-h-[672px] w-full bg-white-900">
        {/* Top Navigation Bar */}
        {showHeader && !isMobile && (
          <div className="px-16 pb-6 pt-20 md:px-[37px] md:pt-[40px]">
            <div className="h-[15px] w-[120px] animate-[pulse_0.6s_ease-in-out_infinite] rounded-[4px] bg-gradient-to-r from-[#e0e0e0] to-[#fafafa] md:w-[164px]" />
          </div>
        )}

        {/* Sidebar and Top Categories */}
        <div className="flex flex-col md:flex-row">
          {/* Left Sidebar */}
          {showFilters && (
            <div className="border-grey-100 hidden w-full space-y-[20px] border-b-1 px-16 py-16 md:block md:w-1/5 md:space-y-[30px] md:border-b-0 md:px-[54px]">
              {/* Title */}
              <div className="h-[28px] w-[62px] animate-[pulse_0.6s_ease-in-out_infinite] rounded-[8px] bg-gradient-to-r from-[#e0e0e0] to-[#fafafa]" />
              {/* Filter rows */}
              <div className="space-y-[15px] md:space-y-[30px]">
                {/* Row 1 - 62px width */}
                <div className="flex items-center gap-3">
                  <div className="h-[18px] w-[62px] animate-[pulse_0.6s_ease-in-out_infinite] rounded-[4px] bg-gradient-to-r from-[#e0e0e0] to-[#fafafa]" />
                  <div className="ml-auto size-[20px] animate-[pulse_0.6s_ease-in-out_infinite] rounded-[8px] bg-gradient-to-r from-[#e0e0e0] to-[#fafafa] opacity-50" />
                </div>
                {/* Row 2 - 62px width */}
                <div className="flex items-center gap-3">
                  <div className="h-[18px] w-[62px] animate-[pulse_0.6s_ease-in-out_infinite] rounded-[4px] bg-gradient-to-r from-[#e0e0e0] to-[#fafafa]" />
                  <div className="ml-auto size-[20px] animate-[pulse_0.6s_ease-in-out_infinite] rounded-[8px] bg-gradient-to-r from-[#e0e0e0] to-[#fafafa] opacity-50" />
                </div>
                {/* Row 3 - 107px width */}
                <div className="flex items-center gap-3">
                  <div className="h-[18px] w-[107px] animate-[pulse_0.6s_ease-in-out_infinite] rounded-[4px] bg-gradient-to-r from-[#e0e0e0] to-[#fafafa]" />
                  <div className="ml-auto size-[20px] animate-[pulse_0.6s_ease-in-out_infinite] rounded-[8px] bg-gradient-to-r from-[#e0e0e0] to-[#fafafa] opacity-50" />
                </div>
                {/* Row 4 - 107px width */}
                <div className="flex items-center gap-3">
                  <div className="h-[18px] w-[107px] animate-[pulse_0.6s_ease-in-out_infinite] rounded-[4px] bg-gradient-to-r from-[#e0e0e0] to-[#fafafa]" />
                  <div className="ml-auto size-[20px] animate-[pulse_0.6s_ease-in-out_infinite] rounded-[8px] bg-gradient-to-r from-[#e0e0e0] to-[#fafafa] opacity-50" />
                </div>
                {/* Row 5 - 107px width */}
                <div className="flex items-center gap-3">
                  <div className="h-[18px] w-[107px] animate-[pulse_0.6s_ease-in-out_infinite] rounded-[4px] bg-gradient-to-r from-[#e0e0e0] to-[#fafafa]" />
                  <div className="ml-auto size-[20px] animate-[pulse_0.6s_ease-in-out_infinite] rounded-[8px] bg-gradient-to-r from-[#e0e0e0] to-[#fafafa] opacity-50" />
                </div>
              </div>
            </div>
          )}

          {/* Main Content Area */}
          <div className="flex-1 px-16 md:px-8">
            {/* Search and Filter Section */}
            {showHeader && !isMobile && (
              <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="h-[27px] w-full animate-[pulse_0.6s_ease-in-out_infinite] rounded-[8px] bg-gradient-to-r from-[#e0e0e0] to-[#fafafa] md:mb-6 md:max-w-[638px]" />
                {/* Top Action Button */}
                <div className="flex justify-end md:mb-8">
                  <div className="h-[36px] w-[150px] animate-[pulse_0.6s_ease-in-out_infinite] rounded-[8px] bg-gradient-to-r from-[#e0e0e0] to-[#fafafa] md:h-[41px] md:w-[198px]" />
                </div>
              </div>
            )}
            {/* Horizontal Category Scroll */}
            <div className="scrollbar-hide mb-12 mt-16 flex gap-[11px] overflow-x-auto md:mt-0">
              <CategorySkeleton />
              <CategorySkeleton />
              <CategorySkeleton />
              <CategorySkeleton />
              <CategorySkeleton />
              <CategorySkeleton />
              <CategorySkeleton />
            </div>

            {/* Mobile Bar - between categories and product grid */}
            {isMobile && (
              <div className="mb-12 h-[24px] w-[328px] animate-[pulse_0.6s_ease-in-out_infinite] rounded-[4px] bg-gradient-to-r from-[#e0e0e0] to-[#fafafa] opacity-50" />
            )}

            {/* Product Grid */}
            <div className="grid grid-cols-2 gap-12 pb-12 md:grid-cols-3 md:gap-[16px] xl:grid-cols-4">
              {Array.from({ length: productCount }, (_, i) => (
                <ProductCardSkeleton key={i} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

PlpSkeleton.propTypes = {
  // eslint-disable-next-line react/boolean-prop-naming
  showHeader: PropTypes.bool,
  // eslint-disable-next-line react/boolean-prop-naming
  showFilters: PropTypes.bool,
  productCount: PropTypes.number,
  // eslint-disable-next-line react/boolean-prop-naming
  isMobile: PropTypes.bool,
  // eslint-disable-next-line react/boolean-prop-naming
  hasBanner: PropTypes.bool,
  // eslint-disable-next-line react/boolean-prop-naming
  isHomePage: PropTypes.bool,
};

export default PlpSkeleton;
